<template>
  <!-- 外層容器 -->
  <div class="contact-container" ref="contactBlock">
    <!-- 主要卡片：左右分佈 -->
    <div class="contact-card animated-contact">
      <!-- 左側：聯絡方式 -->
      <div class="contact-left">
        <h1>聯絡我們</h1>
        <p>有任何問題請隨時透過以下方式聯絡我們！</p>
        <ul>
          <li v-if="settings && settings.COMPANY_PHONE">
            📞 電話：
            <a :href="`tel:${settings.COMPANY_PHONE}`">{{ settings.COMPANY_PHONE }}</a>
          </li>
          <!--傳真-->
          <li v-if="settings && settings.COMPANY_FAX">
            📠 傳真：
            <a :href="`fax:${settings.COMPANY_FAX}`">{{ settings.COMPANY_FAX }}</a>
          </li>
          <li v-if="settings && settings.COMPANY_EMAIL">
            📧 信箱：
            <a :href="`mailto:${settings.COMPANY_EMAIL}`">{{ settings.COMPANY_EMAIL }}</a>
          </li>
          <li v-if="settings && settings.COMPANY_ADDRESS">
            🏢 地址：{{ settings.COMPANY_ADDRESS }}
          </li>
        </ul>
        <img
  
          ref="image"
          loading="lazy"
          :src="contactImg ? `${contactImg}` : null"
          :alt="settings ? `${settings.COMPANY_NAME} (${settings.COMPANY_NAMEN}) 聯絡我們` : '聯絡我們'"
        />
      </div>

      <!-- 右側：表單 -->
      <div class="contact-right">
        <h2>留下您的訊息</h2>
        <!-- 用 handleSubmit() 送出 -->
        <form @submit.prevent="handleSubmit">
          <div class="form-group">
            <label for="name">姓名</label>
            <input
              type="text"
              id="name"
              v-model="contactForm.name"
              placeholder="請輸入您的姓名"
              required
            />
          </div>
          <div class="form-group">
            <label for="email">電子郵件</label>
            <input
              type="email"
              id="email"
              v-model="contactForm.email"
              placeholder="請輸入您的電子郵件"
              required
            />
          </div>
          <div class="form-group">
            <label for="message">訊息</label>
            <textarea
              id="message"
              v-model="contactForm.message"
              placeholder="請輸入您的訊息"
              required
            ></textarea>
          </div>

          <!-- 錯誤訊息顯示 -->
          <div v-if="errorMsg" class="error-message">
            {{ errorMsg }}
          </div>
          <button type="submit" class="submit-btn" :disabled="isSending">
            {{ isSending ? '寄送中...' : '送出' }}
          </button>
        </form>
      </div>
    </div>
      <!-- 地圖容器，放在卡片下方 -->
      <div id="map" style="width: 100%; height: 400px;"></div>
  </div>
</template>

<script setup>
import { ref , defineOptions , computed, onMounted, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'           // 若你使用 Vuex
import axios from 'axios'
import Swal from 'sweetalert2'
import { usePageStore } from '@/store/pageStore.js'


defineOptions({
   name: 'ContactPage'
})

// 1. 從 Vuex 取得 store 與需要的 getters, state
const store = useStore()
//const imagePath = computed(() => store.getters.imagePath)
const userProfile = computed(() => store.state.userProfile)

// 2. 如果你的全域設定值掛在 app.config.globalProperties.$Settings
const instance = getCurrentInstance()
const settings = instance.appContext.config.globalProperties.$Settings
let contactImg = ref(null);

// 3. 定義所需的響應式變數
const contactBlock = ref(null)
const contactForm = ref({
  name: '',
  email: '',
  message: '',
})
const errorMsg = ref('')
const isSending = ref(false)

const COMPANY_GOOGLE_APP_KEY = instance.appContext.config.globalProperties.$Settings.COMPANY_GOOGLE_APP_KEY

//轉浮點數
let COMPANY_GOOGLE_LAT = instance.appContext.config.globalProperties.$Settings.COMPANY_GOOGLE_LAT
let COMPANY_GOOGLE_LNG = instance.appContext.config.globalProperties.$Settings.COMPANY_GOOGLE_LNG
const COMPANY_NAME = instance.appContext.config.globalProperties.$Settings.COMPANY_NAME


// 4. onMounted：做 IntersectionObserver & 帶入預設資料 & 動態載入 Google Maps
onMounted(async () => {
  // 資料載入
  const pageStore = usePageStore()
  await pageStore.fetchContactImg();
  contactImg.value = pageStore.contactImg;

  // (a) IntersectionObserver：淡入效果
  if (contactBlock.value) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('container-visible')
          }
        })
      },
      { threshold: 0.1 }
    )
    observer.observe(contactBlock.value)
  }

  // (b) 若 userProfile 有 name/email，就預先帶入表單
  if (userProfile.value?.username) {
    contactForm.value.name = userProfile.value.username
  }
  if (userProfile.value?.email) {
    contactForm.value.email = userProfile.value.email
  }
  // (c) 動態載入 Google Maps <script>
  //    你也可以從後端 / 資料庫 取得 key 再拼接
  // 1) 檢查 google.maps 是否已載入
  if(COMPANY_GOOGLE_APP_KEY && COMPANY_GOOGLE_LAT && COMPANY_GOOGLE_LNG) {
    COMPANY_GOOGLE_LAT = parseFloat(COMPANY_GOOGLE_LAT)
    COMPANY_GOOGLE_LNG = parseFloat(COMPANY_GOOGLE_LNG)
    if (window.google && window.google.maps) {
    initMap()
  } else {
    // 如果還沒載入，可以動態插入 script，
    // 或者在 main.js 裡插入 script，但不使用 callback=initMap
    loadMapScript(() => {
      initMap()
    })
  }
  }
})

function initMap() {
  /* global google */
  const mapDiv = document.getElementById('map')
  if (!mapDiv) {
    console.error('找不到 #map')
    return
  }
  const map = new google.maps.Map(mapDiv, {
    center: { lat: COMPANY_GOOGLE_LAT, lng: COMPANY_GOOGLE_LNG},
    zoom: 16
  })
  new google.maps.Marker({
    position: { lat: COMPANY_GOOGLE_LAT, lng: COMPANY_GOOGLE_LNG },
    map,
    title: COMPANY_NAME
  })
}

// 這是示例，若你需要在組件裡動態載入 script
function loadMapScript(callback) {
  const script = document.createElement('script')
  script.src = 'https://maps.googleapis.com/maps/api/js?key='+COMPANY_GOOGLE_APP_KEY
  script.async = true
  script.defer = true
  script.onload = () => {
    if (callback) callback()
  }
  document.body.appendChild(script)
}

// 5. 表單送出
async function handleSubmit() {
  // 檢查必填
  if (!contactForm.value.name || !contactForm.value.email || !contactForm.value.message) {
    errorMsg.value = '請完整填寫姓名、電子郵件與訊息'
    return
  }
  errorMsg.value = ''
  isSending.value = true

  const payload = {
    name: contactForm.value.name,
    email: contactForm.value.email,
    message: contactForm.value.message,
  }

  // 若 userProfile 有額外 phone 或 user_id，就一起帶
  if (userProfile.value) {
    if (userProfile.value.phone) {
      payload.phone = userProfile.value.phone
    }
    if (userProfile.value.user_id) {
      payload.user_id = userProfile.value.user_id
    }
  }

  try {
    const res = await axios.post('/api/email/contactSend', payload)
    if (res.data.success) {
      // 清空表單
      contactForm.value.name = ''
      contactForm.value.email = ''
      contactForm.value.message = ''

      Swal.fire({
        icon: 'success',
        title: '訊息已送出',
        text: '我們將盡快回覆您的訊息，謝謝！',
        timer: 2000
      })
    } else {
      alert(res.data.message || '寄信失敗，請稍後再試。')
    }
  } catch (err) {
    console.error(err)
    alert('寄信失敗，請稍後再試。')
  } finally {
    isSending.value = false
  }
}
</script>

<style scoped>
.error-message {
  font-size: 1.3rem;
  color: red;
}

/* 其他樣式保持不變 */
.contact-left img {
  width: 100%;
  max-width: 220px;
  height: auto;
  margin-top: 20px;
  object-fit: cover;
  transition: transform 0.3s ease;
  transform: rotateX(10deg);
  transform-origin: center bottom;
  border: 5px solid #fff;
  border-radius: 10px;
  box-shadow: 0 15px 25px rgba(0, 0, 0, 0.3);
}

.contact-left img:hover {
  transform: rotateX(0deg);
}

.contact-container {
  overflow-x: hidden;
  max-width: 1200px;
  margin: 0 auto;
  padding: 20px;
  opacity: 0;
  transform: translateY(50px) rotateX(8deg);
  transform-origin: top;
  transition: opacity 1.2s ease, transform 1.2s ease;
  perspective: 800px;
}

.contact-container.container-visible {
  opacity: 1;
  transform: translateY(0) rotateX(0);
}

.contact-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 30px;
  background-color: #f9f9f9;
  border-radius: 10px;
  box-shadow: 0 6px 15px rgba(0, 0, 0, 0.1);
  padding: 30px;
  opacity: 0;
  transform: translateY(40px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}

.container-visible .contact-card {
  opacity: 1;
  transform: translateY(0);
}

.contact-left,
.contact-right {
  flex: 1;
  min-width: 300px;
}

.contact-left ul {
  margin-top: 10px;
  list-style: none;
  padding-left: 0;
}

.contact-left li {
  margin: 5px 0;
}

.form-group {
  margin-bottom: 15px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

input,
textarea {
  width: 100%;
  padding: 10px;
  margin-top: 5px;
  border: 1px solid #ccc;
  border-radius: 5px;
  font-size: 1rem;
}

textarea {
  height: 100px;
  resize: none;
}

.submit-btn {
  display: inline-block;
  padding: 10px 20px;
  background-color: #ff6600;
  color: #fff;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.3s ease, transform 0.3s ease;
}

.submit-btn:hover {
  background-color: #b14801;
  transform: scale(1.05);
}

@media (max-width: 768px) {
  .contact-card {
    flex-direction: column;
  }
}
</style>
